/**
 * Testimonial module
 *
 * Blockquote with source and a rounded image
 */

// Deps
import { createComponent } from "react-fela";
import Carousel from 'react-multi-carousel';
import { gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import { textStyles } from "~/styleguide/typography";

// Components
import WordpressLink, {
    getLinkFromRelationship
} from "~/components/global/links";
import SummaryCard from "~/components/resource/summary/card";
import { ContentMaxWidth } from "~/components/layout/max-width";
import { getClient } from "~/modules/wordpress";
import {useEffect, useState} from "react";

const wp = getClient();

export default function RelatedResourcesSlider({ block }) {
    const {
        heading,
        link,
        link_label,
        link_type,
        link_content,
        resources,
        anchor_id,
        number_resources,
    } = block;

    const taxonomies = resources.map((res) => res.term_id);
    const [items, setItems] = useState([]);

    const parsedLink = getLinkFromRelationship(link_type, link_content, link);

    const responsive = {
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 3,
            slidesToSlide: 1,
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 2,
            slidesToSlide: 1,
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1,
            slidesToSlide: 1,
        }
    };

    const getResources = () => {
        let taxQuery = { relation: "OR" };

        for (let i = 0; i < taxonomies.length; i++) {
            const topic = taxonomies[i];
            taxQuery[i] = Object.assign(
                {
                    terms: [topic]
                },
                {
                    taxonomy: "resource-topics",
                    field: "id"
                }
            );
        }
        return () =>
        wp
            .superquery()
            .post_type([
                "whitepapers",
                "infographics",
                "tools",
                "podcasts",
                "casestudies",
                "webinars",
                "videos",
            ])
            .param("tax_query", taxQuery)
    };

    const getItems = async () => {
        let result = await getResources()()
        if (result && result.length) {
            let new_items = result.sort((a, b) => new Date(`${b.date}`).valueOf() - new Date(`${a.date}`).valueOf());
            new_items.splice(number_resources || 10);
            setItems(new_items);
        }
    }

    useEffect(() => {
            getItems();
        }, []
    );

    return (
        <Container id={anchor_id} className="module-related-resources">
            <ContentMaxWidth>
                <Content>
                    {/* Heading */}
                    {heading && (
                        <Heading
                            extend={textStyles.heading3}
                            dangerouslySetInnerHTML={{
                                __html: heading
                            }}
                        />
                    )}

                    {/* Link Out */}
                    {parsedLink && parsedLink.url && link_label && (
                        <WordpressLink
                            extend={readMoreStyles}
                            url={parsedLink.url}
                            slug={parsedLink.slug}
                            label={link_label + " "}
                            layout="text"
                        />
                    )}
                </Content>

                <RelatedOuter>
                    {items &&
                    items.length ? (
                        <Carousel
                            responsive={responsive}
                            infinite={true}
                            containerClass="first-carousel-container container"
                            deviceType={"desktop"}
                            centerMode={false}
                            partialVisible={false}
                        >
                            {items.map((resource, index) => {
                                return (
                                    <SummaryCard
                                        resource={resource}
                                        key={index}
                                        eyebrow={"type"}
                                        layout={"carousel"}
                                        collectionLength={items.length}
                                        extend={cardStyle}
                                    />
                                );
                            })}
                        </Carousel>
                    ) : null }
                </RelatedOuter>
            </ContentMaxWidth>
        </Container>
    );
}

const Heading = createComponent(
    () => ({
        margin: "0 auto 24px"
    }),
    "h3",
    ["dangerouslySetInnerHTML"]
);

const readMoreStyles = {
    display: "inline-block",
    extend: textStyles.sidebarHeading
};

const cardStyle = {
    width: "calc(100% - 20px)",
    minWidth: "calc(100% - 20px)",
    maxWidth: "calc(100% - 20px)",
    margin: "5px 10px",
    height: "calc(100% - 15px)",
}

const Container = createComponent(
    () => ({
        extend: [fluid("paddingV", gutters.vertical)],

        minWidthTabletLandscape: {
            extend: [fluid("paddingH", gutters.default)]
        }
    }),
    "div",
    ["id"]
);

const Content = createComponent(() => ({
    textAlign: "center",

    maxWidthTabletLandscape: {
        extend: [fluid("paddingH", gutters.default)]
    }
}));

const RelatedOuter = createComponent({
    width: "100%",
    marginTop: 54,

    "& .react-multi-carousel-list": {
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        position: "relative",
    },
    "& .react-multi-carousel-track": {
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "flex",
        flexDirection: "row",
        position: "relative",
        transformStyle: "preserve-3d",
        backfaceVisibility: "hidden",
        willChange: "transform, transition",
    },
    "& .react-multiple-carousel__arrow": {
        position: "absolute",
        outline: 0,
        borderRadius: 35,
        zIndex: 1000,
        border: 0,
        background: "#dce1fd",
        minWidth: 60,
        minHeight: 60,
        opacity: 0.8,
        cursor: "pointer",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        "&:before": {
            height: 28,
            zIndex: 2,
            position: "relative",
        },

        "&:hover": {
            opacity: 1,
            border: "2px solid #3B58F5",

            "&.react-multiple-carousel__arrow--left": {
                "&:before": {
                    content: `url("data:image/svg+xml,%3Csvg width='32' height='30' viewBox='0 0 32 30' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.3333 1.66664L1.53033 14.4696C1.23744 14.7625 1.23744 15.2374 1.53033 15.5303L14.3333 28.3333' stroke='%233B58F5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M31 15L1 15' stroke='%233B58F5' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E%0A")`,
                },
            },

            "&.react-multiple-carousel__arrow--right": {
                "&:before": {
                    content: `url("data:image/svg+xml,%3Csvg width='32' height='30' viewBox='0 0 32 30' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M17.6667 28.3334L30.4697 15.5304C30.7626 15.2375 30.7626 14.7626 30.4697 14.4697L17.6667 1.66671' stroke='%233B58F5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M1 15H31' stroke='%233B58F5' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E%0A")`
                },
            },
        },
    },
    "& .react-multiple-carousel__arrow--left": {
        left: "calc(1% + 1px)",

        "&:before": {
            content: `url("data:image/svg+xml,%3Csvg width='32' height='30' viewBox='0 0 32 30' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.3333 1.66664L1.53033 14.4696C1.23744 14.7625 1.23744 15.2374 1.53033 15.5303L14.3333 28.3333' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M31 15L1 15' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E%0A")`,
        },
    },

    "& .react-multiple-carousel__arrow--right": {
        right: "calc(1% + 1px)",

        "&:before": {
            content: `url("data:image/svg+xml,%3Csvg width='32' height='30' viewBox='0 0 32 30' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M17.6667 28.3334L30.4697 15.5304C30.7626 15.2375 30.7626 14.7626 30.4697 14.4697L17.6667 1.66671' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M1 15H31' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E%0A")`
        },
    },

    "& .react-multi-carousel-item": {
        transformStyle: "preserve-3d",
        backfaceVisibility: "hidden",
    }
});
